import type React from 'react';
import { useMemo, useEffect } from 'react';
import { useUserHasConfluenceAccess } from '@atlassian/jira-confluence-integration-controls';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { IssueType } from '@atlassian/jira-issue-shared-types';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { getConfluencePlaceholderTemplate } from '../../../common/utils';
import { PLACEHOLDER_TEMPLATE_CO_USE_AA_EXPERIMENT_ID } from './index';

type Props = {
	issueType?: IssueType;
	hasActivatedConfluenceWithinFourteenDays: boolean;
};

type Cohort = 'not-enrolled' | 'control_aa' | 'control';

export function useAAExperiment(shouldFire = false) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (shouldFire) {
			// Cohorts can be ["control", "control_aa", "not-enrolled"]
			const cohort = expVal<Cohort>(
				'aa_confluence_placeholder_template_onboarding',
				'cohort',
				'not-enrolled',
			);
			const analyticsEvent = createAnalyticsEvent({});

			if (cohort.startsWith('control')) {
				fireTrackAnalytics(
					analyticsEvent,
					`${PLACEHOLDER_TEMPLATE_CO_USE_AA_EXPERIMENT_ID} exposed`,
					{
						cohort,
					},
				);
			}
		}
	}, [shouldFire, createAnalyticsEvent]);
}

export function PlaceholderTemplateCoUseAAExperiment({
	hasActivatedConfluenceWithinFourteenDays,
	issueType,
}: Props): React.ReactNode {
	const { licensedProducts } = useTenantContext();
	const tenantHasConfluence = licensedProducts?.confluence === true;

	const { hasAccess } = useUserHasConfluenceAccess();
	const template = useMemo(() => getConfluencePlaceholderTemplate(issueType), [issueType]);

	const isAAExperimentEnabled =
		tenantHasConfluence &&
		hasAccess &&
		hasActivatedConfluenceWithinFourteenDays &&
		template !== null;

	useAAExperiment(isAAExperimentEnabled);

	return null;
}
